import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { PLACEMENT_COHORT } from "@src/appV2/redesign/PlacementCandidate/types";
import { addHours, isWithinInterval, startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { useInterviewOnJobCards } from "./useInterviewOnJobCards";

export type RecruiterScreeningConfig = ReturnType<typeof usePlacementRecruiterScreeningConfig>;

export function usePlacementRecruiterScreeningConfig() {
  const config = useCbhFlag(CbhFeatureFlag.PLACEMENT_RECRUITER_SCREENING, {
    defaultValue: {
      enabled: false,
      showScreeningCard: false,
      showCallMeNow: false,
      legacyDocumentRequirementId: "",
    },
  });
  const calendlyLinks = useCbhFlag(CbhFeatureFlag.SCREENING_CALENDLY_LINKS, {
    defaultValue: {
      default: {
        screeningCalendlyLink: "",
        upskillingCalendlyLink: "",
      },
    },
  });
  const callNowHours = useCbhFlag(CbhFeatureFlag.RECRUITER_SCREENING_CALL_NOW_HOURS, {
    defaultValue: {
      start: 8,
      end: 20,
      daysOfWeek: [
        DayOfTheWeek.MONDAY,
        DayOfTheWeek.TUESDAY,
        DayOfTheWeek.WEDNESDAY,
        DayOfTheWeek.THURSDAY,
        DayOfTheWeek.FRIDAY,
      ],
    },
  });
  const { cohorts, enabled: interviewsOnJobCardsEnabled } = useInterviewOnJobCards();

  // Return early if the feature flag returns invalid configuration
  if (!callNowHours.daysOfWeek?.length || callNowHours.start >= callNowHours.end) {
    return {
      ...config,
      showCallMeNow: false,
      calendlyLinks,
    };
  }

  // 8 AM - 8 PM PST
  const currentPST = utcToZonedTime(new Date(), "America/Los_Angeles");
  const daysOfWeek = callNowHours.daysOfWeek.map((day) => Object.values(DayOfTheWeek).indexOf(day));
  const currentDayOfWeekIndex = (currentPST.getDay() + 6) % 7; // re-index to 0-indexed based on monday

  // Create start/end times in Pacific timezone, then convert to UTC for proper comparison
  const startOfDayPST = zonedTimeToUtc(
    addHours(startOfDay(currentPST), callNowHours.start),
    "America/Los_Angeles"
  );

  const endOfDayPST = zonedTimeToUtc(
    addHours(startOfDay(currentPST), callNowHours.end),
    "America/Los_Angeles"
  );

  const withinRecruiterWorkingHours =
    daysOfWeek.includes(currentDayOfWeekIndex) &&
    isWithinInterval(new Date(), {
      start: startOfDayPST,
      end: endOfDayPST,
    });

  const showCallMeNow = config.showCallMeNow && withinRecruiterWorkingHours;

  const isInterviewsOnJobCardsCohort =
    interviewsOnJobCardsEnabled && cohorts.includes(PLACEMENT_COHORT.INTERVIEWS_ON_JOB_CARDS);

  if (isInterviewsOnJobCardsCohort) {
    return {
      ...config,
      enabled: false,
      showScreeningCard: false,
      showCallMeNow: false,
      calendlyLinks,
    };
  }

  return {
    ...config,
    showCallMeNow,
    calendlyLinks,
  };
}
